import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { searchSettings, type SettingsSearchEntry } from './settingsSearch';
import { confirmDiscard } from './useUnsavedGuard';

/**
 * Search across every setting, at the top of the settings nav.
 *
 * The owner's problem was never "which page is Printing on" — it was "where
 * did you put rounding". So a hit navigates to the page AND to the section:
 * the entry's anchor becomes the URL hash and `SettingsLayout` scrolls to it
 * once that page has rendered.
 *
 * While there is a query the results REPLACE the groups rather than floating
 * over them. A dropdown would have to solve overlap, z-index and RTL placement
 * to say the same thing, and on a phone — where this box lives inside the
 * folding settings menu — it would cover the page it is about to open.
 *
 * `onReveal` opens whatever has folded this box away. A shut phone menu is
 * `display: none`, and `focus()` on a hidden input does nothing — so "/" and
 * ctrl+K went dead on every compact screen that has a keyboard (a desktop
 * window under 768px wide, or one zoomed past 1024×512), where before the
 * fold the box had always been on screen.
 */
export function SettingsSearchBox({ onReveal }: { onReveal?: () => void } = {}) {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const results = useMemo(
    () => searchSettings(query, (key) => t(key), { can }),
    // `i18n.language` is a real dependency: matching runs over the TRANSLATED
    // label, so the same query finds different things in Arabic than in English.
    [query, i18n.language, t, can],
  );

  /*
   * "/" to search, the convention every app the owner uses already has — and
   * ctrl/cmd+K for the same reason. Ignored while a field has focus, or the
   * slash would never reach the field it was typed into.
   */
  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement);

      /*
       * NOT WHILE A DIALOG IS OPEN. These listeners are on `window` for as long
       * as the settings area is mounted, so a press inside the printer dialog
       * reached them too — and "/" on a button in that dialog yanked focus to a
       * search box behind the modal, which is a trap with no way back but Tab.
       * A modal owns the keyboard while it is up.
       */
      const inDialog =
        target instanceof HTMLElement &&
        target.closest('[role="dialog"], [aria-modal="true"]') !== null;
      if (inDialog) return;

      const wantsSearch =
        (event.key === '/' && !typing) || ((event.ctrlKey || event.metaKey) && event.key === 'k');
      if (!wantsSearch) return;

      event.preventDefault();
      const input = inputRef.current;
      if (!input) return;
      // No boxes means not rendered: open the fold first, synchronously, so
      // the input is on screen by the time focus is asked of it.
      if (onReveal && input.getClientRects().length === 0) flushSync(onReveal);
      input.focus();
      input.select();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onReveal]);

  function open(entry: SettingsSearchEntry): void {
    // A jump inside the page already open loses nothing, so it must not ask —
    // see `shouldConfirmNavigation`, which holds the same rule for links.
    const samePage = entry.path === location.pathname;
    if (!samePage && !confirmDiscard()) return;

    navigate(entry.anchor ? `${entry.path}#${entry.anchor}` : entry.path);
    setQuery('');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      setQuery('');
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const first = results[0];
      if (first) open(first);
    }
  }

  return (
    <div className="mb-3 compact:mb-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
          style={{ insetInlineStart: '0.8rem' }}
          aria-hidden
        />
        <input
          ref={inputRef}
          // `type="search"`, so a phone keyboard offers the right key and the
          // browser's own clear button appears.
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('settings.search.placeholder')}
          aria-label={t('settings.search.label')}
          className="w-full rounded-full border border-subtle bg-input py-2 pe-4 ps-9 text-sm text-content outline-none placeholder:text-content-muted focus:border-primary"
        />
      </div>

      {query.trim() ? (
        results.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {results.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => open(entry)}
                  className="block w-full rounded-control px-3 py-2 text-start text-sm text-content transition-colors hover:bg-hover"
                >
                  <span className="block">{t(entry.labelKey)}</span>
                  {/* Which page it lives on. Without it a result is a name with
                      no address, and the owner learns nothing for next time. */}
                  <span className="block text-xs text-content-muted">{t(entry.pageKey)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          // A real state, not an empty list: an empty list looks like a screen
          // that is still loading.
          <p className="mt-2 px-3 py-2 text-xs text-content-muted">
            {t('settings.search.noResults', { query: query.trim() })}
          </p>
        )
      ) : null}
    </div>
  );
}
